import { useEffect, useState } from 'react';

import { CheckCircle, Loader2, AlertTriangle } from 'lucide-react';
import { useQueryClient } from 'react-query';
import { toast as sonnerToast } from 'sonner';

import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useUpdateCase } from '@/api/mutations/useUpdateCase';
import { useGetCaseIsPendingUpload } from '@/api/queries/useGetCaseIsPendingUpload';
import useGetFiles from '@/api/queries/useGetFiles';
import { QueryEntity } from '@/api/types';
import { Button } from '@/components/atoms/new-button';

interface ConfirmationProps {
  detailsChanged: boolean;
  totalUnprocessedDocs: number;
  caseId: string;
  caseIssues: string;
  caseParties: string;
  caseContext: string;
  caseTimePeriod: string;
  closeModal: () => void;
}

export const Confirmation = ({
  detailsChanged,
  totalUnprocessedDocs,
  caseId,
  caseIssues,
  caseParties,
  caseContext,
  caseTimePeriod,
  closeModal,
}: ConfirmationProps) => {
  const { mutateAsync, isLoading } = useUpdateCase();
  const [result, setResult] = useState('');
  const queryClient = useQueryClient();

  const handleUpdateCase = async () => {
    await mutateAsync(
      {
        caseId: caseId,
        updateDetailsActive: detailsChanged,
        caseIssues: caseIssues,
        caseParties: caseParties,
        caseContext: caseContext,
        caseTimePeriod: caseTimePeriod,
      },
      {
        onSuccess: () => {
          setResult('success');
          setTimeout(() => {
            closeModal();
          }, 1000);
        },
        onError: () => {
          setResult('failed');
          setTimeout(() => {
            closeModal();
          }, 1000);
        },
        onSettled: () => {
          queryClient.invalidateQueries([QueryEntity.Case, caseId]);
        },
      },
    );
    trackCustomEvent(TrackingEvents.UpdateCase, { caseId });
  };

  // This function checks whether there is an ongoing upload from the user.
  const { data: isPendingUploadData, refetch: isPendingUploadRefetch } = useGetCaseIsPendingUpload(caseId);
  const isPendingUpload = async (): Promise<boolean> => {
    const { data } = await isPendingUploadRefetch();
    return data === true;
  };

  // Processes cases only where an ongoing upload from the user is not present.
  const processCaseWrapper = async () => {
    const isUploadOngoing = await isPendingUpload();
    if (isUploadOngoing) {
      sonnerToast.info('An upload is still in progress. Please wait and try again later.', {
        duration: 3000,
        position: 'top-right',
        richColors: true,
      });
    } else {
      sonnerToast.success('Case processing.', {
        duration: 3000,
        position: 'top-right',
        richColors: true,
      });
      handleUpdateCase();
    }
  };

  // Messy, but a quick fix until Redis is implemented.
  const { refetch: refetchFiles } = useGetFiles(caseId, 1);
  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;
    if (isPendingUploadData === true) {
      interval = setInterval(() => {
        refetchFiles();
        isPendingUploadRefetch();
      }, 10_000);
    } else if (isPendingUploadData !== true) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [isPendingUploadData, refetchFiles, isPendingUploadRefetch]);

  return (
    <div className="px-8 py-4 text-sm text-gray-800">
      {isLoading ? (
        <div className="flex h-52 flex-col items-center justify-center">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      ) : result === 'failed' ? (
        <div className="flex h-52 flex-col items-center gap-4 pt-10">
          <p className="flex items-center gap-2 text-xl font-semibold">
            <AlertTriangle className="h-5 w-5 text-red-700" />
            Update Failed
          </p>
          <p className="text-sm text-gray-500">
            Make sure there are no current updates running on this case. If you need help, please contact the Wexler
            team.
          </p>
        </div>
      ) : result === 'success' ? (
        <div className="flex h-52 flex-col items-center gap-4 pt-10">
          <p className="flex items-center gap-2 text-xl font-semibold">
            <CheckCircle className="h-5 w-5 text-green-700" />
            Update In Progress
          </p>
          <p className="text-sm text-gray-500">Please check back later, you can go back to your case now.</p>
        </div>
      ) : (
        <>
          <p className="text-xs text-gray-500">
            Make sure you have made all of the necessary changes to your case details. Updating a case can be time
            consuming.
          </p>
          <p className="mt-2 text-xs text-gray-500">
            If there are unwanted documents in this update please remove them from the Documents tab.
          </p>

          <div className="mt-4 border border-x-0 border-b-0 pt-4">
            <p className="">
              This update will include: {detailsChanged && <b className="underline">case details</b>}
              {detailsChanged && totalUnprocessedDocs > 0 && ', '}
              {totalUnprocessedDocs > 0 && (
                <b className="underline">
                  {totalUnprocessedDocs} {totalUnprocessedDocs === 1 ? 'document' : 'documents'}
                </b>
              )}
            </p>

            <div className="mt-6 mb-2 flex gap-10">
              <Button
                variant="brand"
                size="default"
                disabled={isLoading}
                onClick={async () => {
                  await processCaseWrapper();
                }}
              >
                {isLoading ? 'Updating...' : 'Update'}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
